import { useState } from 'react';
import type { SatelliteLeadManagementSourceType } from '@zg-rentals/ts-satellite-client';

const NO_SOURCES: Array<SatelliteLeadManagementSourceType> = [];

type Params = {
  availableSources: Array<SatelliteLeadManagementSourceType> | undefined;
  conversationIdToFilterBy: string | null;
  listingAliases: Array<string>;
};

type HeldSources = {
  listingAliasesKey: string;
  sources: Array<SatelliteLeadManagementSourceType> | undefined;
};

/**
 * Holds the table response's `availableSources` for the current listing selection. The backend
 * computes it before the folder/status/criteria filters, so it only changes with the selection;
 * holding it keeps the lead source UI from dropping out while a filter change refetches. A
 * search-narrowed response is ignored because the backend scopes its set to that one conversation,
 * and an empty set for the same selection is ignored because it only comes back on a response with
 * no leads, where nothing renders from it. When the selection changes, the previous set is kept
 * until the new selection's first response lands and is then replaced by it even when empty, so the
 * source UI never blinks out between the two — a blink here remounts the listings selector while
 * the user is still picking listings.
 */
export default function useLeadManagementAvailableSources({
  availableSources,
  conversationIdToFilterBy,
  listingAliases,
}: Params): Array<SatelliteLeadManagementSourceType> {
  const listingAliasesKey = listingAliases.join(',');
  const [held, setHeld] = useState<HeldSources>({ listingAliasesKey, sources: undefined });
  const isSameSelection = held.listingAliasesKey === listingAliasesKey;
  const isUnscopedResponse = !conversationIdToFilterBy && availableSources !== undefined;
  const adoptsResponse = isUnscopedResponse && (!isSameSelection || availableSources.length > 0);
  const sources = adoptsResponse ? availableSources : held.sources;

  if (adoptsResponse && (!isSameSelection || sources !== held.sources)) {
    setHeld({ listingAliasesKey, sources });
  }

  return sources ?? NO_SOURCES;
}
